import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { ArrowLeft, Pencil } from "lucide-react";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { parseLastSeenTimestamp } from "utils/parseLastSeenTimestamp";
import { Link, useParams } from "react-router-dom";
import { BASE_API_URL } from "store/Me";
import { AdminTable } from "components/shared/AdminTable";
import { PageControl } from "components/shared/PageControl";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import {
  statusBadgeOutlineClass,
  type StatusColor,
} from "shared/utils/statusChipColor";

type ProxyStatus = "Unknown" | "Online" | "Offline" | "Error";

const KEY_REQUEST_STATUS_COLOR: Record<string, StatusColor> = {
  KeyIssueSucceeded: "success",
  Denied: "error",
  KeyIssueFailed: "error",
  KeyIssueRequested: "warning",
  Approved: "info",
  Initial: "default",
};

const KEY_REQUEST_CREDENTIAL_LABEL: Record<string, string> = {
  remoteOpen: "Remote Open",
  mobileKey: "Mobile Key",
  getPin: "Get PIN",
  physicalKey: "Physical Key",
};

type SiteProxyDetail = {
  id: string;
  name: string;
  siteId: string;
  siteName: string;
  status: ProxyStatus;
  error?: string;
  username?: string;
  sharedKeyName?: string;
  sharedKey?: string;
  version: number;
  softwareVersion?: string;
  lastSeenTimestamp?: string;
  lastHealthyTimestamp?: string;
};

type SiteProxyOutageEvent = {
  id: string;
  timestamp: string;
  lastObservedTimestamp: string;
  observedCount: number;
  resolvedTimestamp?: string;
  error?: string;
  status: ProxyStatus;
  errorType: string;
  alertSent: boolean;
};

type ProxySiteKeyRequest = {
  id: string;
  status: string;
  credentialType: string;
  credentialId?: string;
  error?: string;
  guestId?: string;
  guestName?: string;
  details?: string;
  note?: string;
  createdTimestamp: string;
  updatedTimestamp: string;
};

type ProxyUpdateRequest = {
  username: string;
  password?: string;
  sharedKeyName: string;
  sharedKey: string;
};

function BackToLocksLink() {
  return (
    <Link
      to="/admin/locks"
      className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
    >
      <ArrowLeft className="size-4" />
      Back to Locks
    </Link>
  );
}

async function getProxy(proxyId: string) {
  const result = await axios.get<SiteProxyDetail>(
    `${BASE_API_URL}/api/proxies/${proxyId}`,
  );
  return result.data;
}

async function getProxyOutages(proxyId: string) {
  const result = await axios.get<SiteProxyOutageEvent[]>(
    `${BASE_API_URL}/api/proxies/${proxyId}/outages?take=100`,
  );
  return result.data;
}

async function getProxyKeyRequests(proxyId: string, siteId: string, page: number, pageSize: number) {
  const skip = page * pageSize;
  try {
    const result = await axios.get<ProxySiteKeyRequest[]>(
      `${BASE_API_URL}/api/proxies/${proxyId}/keyRequests?take=${pageSize}&skip=${skip}`,
    );
    return result.data;
  } catch (error: any) {
    if (error?.response?.status !== 404) {
      throw error;
    }

    // Fallback for environments where the proxy-site key request endpoint is not yet deployed.
    // NOTE: this fetches a limited page and filters client-side — results may be incomplete
    // for high-volume sites. A banner is shown to admins when this path is used.
    const fallback = await axios.get<ProxySiteKeyRequest[]>(
      `${BASE_API_URL}/api/keyRequests?page=0&pageSize=250`,
    );

    const filtered = (fallback.data ?? [])
      .filter((request: any) => request?.site?.id === siteId)
      .slice(0, 25);

    return Object.assign(filtered, { _usedFallback: true });
  }
}

async function updateProxy(proxyId: string, request: ProxyUpdateRequest) {
  const result = await axios.put(
    `${BASE_API_URL}/api/proxies/${proxyId}`,
    request,
  );
  return result.data;
}

export default function AdminProxyPage() {
  const queryClient = useQueryClient();
  const params = useParams<{ proxyId: string }>();
  const proxyId = params.proxyId!;

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"outages" | "key-requests">("outages");
  const [keyRequestPage, setKeyRequestPage] = useState(0);
  const [keyRequestPageSize, setKeyRequestPageSize] = useState(25);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [sharedKeyName, setSharedKeyName] = useState("");
  const [sharedKey, setSharedKey] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    data: proxy,
    isLoading: isProxyLoading,
    isError: isProxyError,
  } = useQuery({
    queryKey: ["site-proxies", "details", proxyId],
    queryFn: () => getProxy(proxyId),
  });

  const {
    data: outages,
    isLoading: isOutagesLoading,
    isError: isOutagesError,
  } = useQuery({
    queryKey: ["site-proxies", "outages", proxyId],
    queryFn: () => getProxyOutages(proxyId),
  });

  const {
    data: keyRequests,
    isLoading: isKeyRequestsLoading,
    isError: isKeyRequestsError,
  } = useQuery({
    queryKey: ["site-proxies", "key-requests", proxyId, keyRequestPage, keyRequestPageSize],
    queryFn: () => getProxyKeyRequests(proxyId, proxy?.siteId ?? "", keyRequestPage, keyRequestPageSize),
    enabled: !!proxy?.siteId,
  });

  useEffect(() => {
    if (!proxy) {
      return;
    }

    setUsername(proxy.username ?? "");
    setSharedKeyName(proxy.sharedKeyName ?? "");
    setSharedKey(proxy.sharedKey ?? "");
  }, [proxy]);

  const { mutate: saveProxy, isPending: isSaving } = useMutation({
    mutationFn: (payload: ProxyUpdateRequest) => updateProxy(proxyId, payload),
    onSuccess: () => {
      setIsEditOpen(false);
      setPassword("");
      queryClient.invalidateQueries({
        queryKey: ["site-proxies", "details", proxyId],
      });
      queryClient.invalidateQueries({ queryKey: ["site-proxies"] });
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Unable to update proxy information.";
      setSaveError(typeof msg === "string" ? msg : JSON.stringify(msg));
    },
  });

  const handleSave = () => {
    const payload: ProxyUpdateRequest = {
      username,
      sharedKeyName,
      sharedKey,
      password: password || undefined, // Omit password if empty
    } as ProxyUpdateRequest;

    saveProxy(payload);
  };

  if (isProxyLoading) {
    return (
      <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
        <header className="pb-5 border-b border-slate-200">
          <BackToLocksLink />
        </header>
        <div className="flex items-center justify-center text-sm bg-white border rounded-lg shadow-sm min-h-80 border-slate-200 text-slate-500">
          Loading proxy...
        </div>
      </main>
    );
  }

  if (isProxyError || !proxy) {
    return (
      <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
        <header className="pb-5 border-b border-slate-200">
          <BackToLocksLink />
        </header>
        <Alert variant="destructive">Unable to load proxy details. Please try again.</Alert>
      </main>
    );
  }

  const isOffline = proxy.status.toLowerCase() === "offline";

  return (
    <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
      <header className="pb-5 border-b border-slate-200">
        <BackToLocksLink />
      </header>

      {isOffline ? (
        <Alert variant="destructive">
          <div className="text-base font-semibold">Offline</div>
          <div className="mt-1 text-sm">Proxy is offline — lock access and key provisioning are unavailable.</div>
          {proxy.lastSeenTimestamp ? (
            <div className="mt-1 text-xs text-slate-500">
              Last online: {parseLastSeenTimestamp(proxy.lastSeenTimestamp)?.toLocaleString() ?? "Unknown"}
            </div>
          ) : null}
        </Alert>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(19rem,24rem)_minmax(0,1fr)]">
        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <div className="px-1">
            <h1 className="text-2xl font-semibold text-slate-900">{proxy.name}</h1>
            <div className="mt-1">
              <ProxyStatusChip status={proxy.status} />
            </div>
          </div>

          <section className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h2 className="text-base font-semibold text-slate-900">Proxy Details</h2>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={`Edit ${proxy.name}`}
                    className="text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                    data-testid="locks-proxy-details-edit-button"
                    onClick={() => {
                      setSaveError(null);
                      setIsEditOpen(true);
                    }}
                    size="icon-sm"
                    type="button"
                    variant="ghost"
                  >
                    <Pencil />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit proxy settings</TooltipContent>
              </Tooltip>
            </div>
            <div className="p-4">
              <table className="w-full text-sm text-left [&_th]:w-2/5 [&_th]:py-2 [&_th]:pr-4 [&_th]:font-medium [&_th]:align-top [&_th]:text-slate-500 [&_td]:py-2 [&_td]:text-slate-800">
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <th>Site</th>
                    <td>{proxy.siteName}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <ProxyStatusChip status={proxy.status} />
                    </td>
                  </tr>
                  <tr>
                    <th>Version</th>
                    <td>{proxy.version}</td>
                  </tr>
                  <tr>
                    <th>Software Version</th>
                    <td>{proxy.softwareVersion || "-"}</td>
                  </tr>
                  <tr>
                    <th>Username</th>
                    <td>{proxy.username || "-"}</td>
                  </tr>
                  <tr>
                    <th>Shared Key Name</th>
                    <td>{proxy.sharedKeyName || "-"}</td>
                  </tr>
                  <tr>
                    <th>Shared Key</th>
                    <td>{maskSecret(proxy.sharedKey)}</td>
                  </tr>
                  <tr>
                    <th>Last Seen</th>
                    <td>
                      {proxy.lastSeenTimestamp
                        ? parseLastSeenTimestamp(proxy.lastSeenTimestamp)?.toLocaleString() ?? "-"
                        : "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Last Healthy</th>
                    <td>
                      {proxy.lastHealthyTimestamp
                        ? parseLastSeenTimestamp(proxy.lastHealthyTimestamp)?.toLocaleString() ?? "-"
                        : "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Error</th>
                    <td>{proxy.error || "-"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </aside>

        <section className="min-w-0">
          <Tabs
            value={activeTab}
            onValueChange={(tabValue) =>
              setActiveTab(tabValue as "outages" | "key-requests")
            }
            data-testid="locks-proxy-details-tabs"
          >
            <TabsList className="justify-start w-full overflow-x-auto overflow-y-hidden">
              <TabsTrigger
                className="shrink-0 data-[state=active]:bg-slate-200!"
                data-testid="locks-proxy-outages-tab"
                value="outages"
              >
                Outage Events
              </TabsTrigger>
              <TabsTrigger
                className="shrink-0 data-[state=active]:bg-slate-200!"
                data-testid="locks-proxy-key-requests-tab"
                value="key-requests"
              >
                Key Requests
              </TabsTrigger>
            </TabsList>

            <TabsContent className="pt-4" value="outages">
              {isOutagesError ? (
                <Alert variant="destructive">Unable to load outage events.</Alert>
              ) : isOutagesLoading ? (
                <div className="text-sm text-slate-500">Loading outage events...</div>
              ) : outages && outages.length > 0 ? (
                <AdminTable aria-label="Proxy outage events">
                  <thead>
                    <tr>
                      <th scope="col">Started</th>
                      <th scope="col">Last Observed</th>
                      <th scope="col">Resolved</th>
                      <th scope="col">Status</th>
                      <th scope="col">Error Type</th>
                      <th scope="col">Observed Count</th>
                      <th scope="col">Alert Sent</th>
                      <th scope="col">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outages.map((event) => (
                      <tr key={event.id}>
                        <td>{parseLastSeenTimestamp(event.timestamp)?.toLocaleString() ?? "-"}</td>
                        <td>{parseLastSeenTimestamp(event.lastObservedTimestamp)?.toLocaleString() ?? "-"}</td>
                        <td>
                          {event.resolvedTimestamp
                            ? parseLastSeenTimestamp(event.resolvedTimestamp)?.toLocaleString() ?? "-"
                            : "Open"}
                        </td>
                        <td>
                          <ProxyStatusChip status={event.status} />
                        </td>
                        <td>{event.errorType}</td>
                        <td>{event.observedCount}</td>
                        <td>{event.alertSent ? "Yes" : "No"}</td>
                        <td>{event.error || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </AdminTable>
              ) : (
                <p className="text-sm text-slate-500">No outage events found.</p>
              )}
            </TabsContent>
            <TabsContent className="pt-4" value="key-requests">
              {isKeyRequestsError ? (
                <Alert variant="destructive">Unable to load key requests.</Alert>
              ) : isKeyRequestsLoading ? (
                <div className="text-sm text-slate-500">Loading key requests...</div>
              ) : keyRequests && keyRequests.length > 0 ? (
                <>
                  {(keyRequests as any)._usedFallback ? (
                    <Alert className="mb-3" variant="warning">
                      Showing results from the fallback endpoint — data may be incomplete for high-volume sites.
                    </Alert>
                  ) : null}
                  <AdminTable aria-label="Proxy key requests">
                    <thead>
                      <tr>
                        <th scope="col">Updated</th>
                        <th scope="col">Guest</th>
                        <th scope="col">Status</th>
                        <th scope="col">Type</th>
                        <th scope="col">Credential ID</th>
                        <th scope="col">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keyRequests.map((request) => (
                        <tr key={request.id}>
                          <td>
                            <div className="text-[13px] whitespace-nowrap">
                              {new Date(request.updatedTimestamp).toLocaleString()}
                            </div>
                            <div className="text-xs text-slate-500">{request.id}</div>
                          </td>
                          <td>
                            {request.guestName && <div>{request.guestName}</div>}
                            {request.guestId && (
                              <div className="text-xs text-slate-500">{request.guestId}</div>
                            )}
                            {!request.guestName && !request.guestId && "—"}
                          </td>
                          <td>
                            {request.status && (
                              <Badge
                                className={statusBadgeOutlineClass(
                                  KEY_REQUEST_STATUS_COLOR[request.status as string] ?? "default",
                                )}
                                variant="outline"
                              >
                                {request.status}
                              </Badge>
                            )}
                          </td>
                          <td>{KEY_REQUEST_CREDENTIAL_LABEL[request.credentialType as string] ?? request.credentialType ?? "—"}</td>
                          <td>
                            {request.credentialId ? (
                              <div
                                className="overflow-hidden font-mono text-xs max-w-50 text-ellipsis whitespace-nowrap"
                                title={request.credentialId}
                              >
                                {request.credentialId}
                              </div>
                            ) : "—"}
                          </td>
                          <td>
                            {request.error ? (
                              <span className="text-xs font-medium text-red-600">{request.error}</span>
                            ) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </AdminTable>
                  <div className="pt-3">
                    <PageControl
                      currentPage={keyRequestPage}
                      isLoading={isKeyRequestsLoading}
                      onNext={() => setKeyRequestPage((p) => p + 1)}
                      onPrev={() => setKeyRequestPage((p) => Math.max(0, p - 1))}
                      pageSize={keyRequestPageSize}
                      setPageSize={(size) => { setKeyRequestPageSize(size); setKeyRequestPage(0); }}
                      disablePrev={keyRequestPage === 0}
                      disableNext={(keyRequests?.length ?? 0) < keyRequestPageSize}
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500">No recent key requests for this site.</p>
              )}
            </TabsContent>
          </Tabs>
        </section>
      </div>

      <Dialog
        open={isEditOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setIsEditOpen(false);
          }
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          showCloseButton={false}
          className="sm:max-w-lg"
        >
          <DialogHeader>
            <DialogTitle>Edit Site Proxy</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
          {saveError ? (
            <Alert variant="destructive">
              {saveError}
            </Alert>
          ) : null}
          <div className="grid gap-2">
            <Label htmlFor="locks-proxy-edit-username-input">Username</Label>
            <Input
              id="locks-proxy-edit-username-input"
              value={username}
              data-testid="locks-proxy-edit-username-input"
              onChange={(event) => setUsername(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="locks-proxy-edit-password-input">Password</Label>
            <Input
              id="locks-proxy-edit-password-input"
              type="password"
              value={password}
              data-testid="locks-proxy-edit-password-input"
              onChange={(event) => setPassword(event.target.value)}
            />
            <span className="text-xs text-muted-foreground">
              Leave unchanged if not updating credentials
            </span>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="locks-proxy-edit-shared-key-name-input">
              Shared Key Name
            </Label>
            <Input
              id="locks-proxy-edit-shared-key-name-input"
              value={sharedKeyName}
              data-testid="locks-proxy-edit-shared-key-name-input"
              onChange={(event) => setSharedKeyName(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="locks-proxy-edit-shared-key-input">Shared Key</Label>
            <Input
              id="locks-proxy-edit-shared-key-input"
              type="password"
              value={sharedKey}
              data-testid="locks-proxy-edit-shared-key-input"
              onChange={(event) => setSharedKey(event.target.value)}
            />
            <span className="text-xs text-muted-foreground">
              Leave unchanged if not updating
            </span>
          </div>
          </div>
          <DialogFooter>
          <Button
            onClick={() => setIsEditOpen(false)}
            disabled={isSaving}
            data-testid="locks-proxy-edit-cancel-button"
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            data-testid="locks-proxy-edit-save-button"
            onClick={handleSave}
            disabled={isSaving}
            type="button"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function ProxyStatusChip({ status }: { status: string }) {
  const normalized = (status || "Unknown").toLowerCase();

  let color: StatusColor = "default";
  if (normalized === "online") {
    color = "success";
  } else if (normalized === "offline" || normalized === "error") {
    color = "error";
  }

  return (
    <Badge className={statusBadgeOutlineClass(color)} variant="outline">
      {status}
    </Badge>
  );
}

function maskSecret(value?: string) {
  if (!value) {
    return "-";
  }

  if (value.length <= 4) {
    return "****";
  }

  return `${"*".repeat(value.length - 4)}${value.slice(-4)}`;
}
